/**
 * Permission names the web app checks. The API is the authority
 * (apps/api/src/common/permissions.ts, sent in auth/me); this list only names them so
 * routes and menus can be guarded. A name the API never grants just hides the menu
 * entry — it never opens anything. Cabinet/folder grants are enforced by the API.
 * Aligned with D1 (docs/sprints/D1.md "Differences").
 */
export const P = {
  docsView: 'documents.view',
  docsUpload: 'documents.upload',
  docsEdit: 'documents.edit',
  docsDelete: 'documents.delete',
  docsPurge: 'documents.purge',
  docsDownload: 'documents.download',
  docsPrint: 'documents.print',
  docsShare: 'documents.share',
  docsAllCabinets: 'documents.allCabinets',
  /** QXM-SC-04 (Q6): redaction editor + Records › Redactions (Org Admin, Records Officer). */
  docsRedact: 'documents.redact',
  inboxReview: 'inbox.review',
  cabinetsManage: 'cabinets.manage',
  typesManage: 'types.manage',
  tagsManage: 'tags.manage',
  correspondentsManage: 'correspondents.manage',
  rulesManage: 'rules.manage',
  retention: 'retention.manage',
  holds: 'holds.manage',
  dispositionPropose: 'disposition.propose',
  dispositionApprove: 'disposition.approve',
  approvals: 'approvals.decide',
  workflows: 'workflows.manage',
  /** e-Sign: start signing requests (FR-ADM-02; Editor and above by default). */
  esignSend: 'esign.send',
  // QXM document control (PRD §2)
  controlledManage: 'controlled.manage',
  controlledTransition: 'controlled.transition',
  controlledReview: 'controlled.review',
  changesRaise: 'changes.raise',
  acksAssign: 'acks.assign',
  copiesManage: 'copies.manage',
  externalManage: 'external.manage',
  recordsDeclare: 'records.declare',
  // QXM Q8 (PRD §18 RF): physical copies, boxes, files, locations + RFID scans
  physicalScan: 'physical.scan',
  physicalManage: 'physical.manage',
  // QXM Exception Center (PRD §10)
  exceptionsView: 'exceptions.view',
  exceptionsManage: 'exceptions.manage',
  exceptionsWaive: 'exceptions.waive',
  // QXM Q5 (PRD §11–12)
  classificationSet: 'classification.set',
  accessReview: 'access.review',
  evidencePack: 'evidence.pack',
  // QXM Q9 NCR / CAPA (PRD §14)
  capaView: 'capa.view',
  capaRaise: 'capa.raise',
  capaManage: 'capa.manage',
  capaVerify: 'capa.verify',
  // QXM Q10 recurring controls (PRD §15)
  controlsView: 'controls.view',
  controlsManage: 'controls.manage',
  controlsVerify: 'controls.verify',
  // QXM Q11 AI (PRD §16): run AI gap checks (Ask QXM needs documents.view; both hidden unless the AI is switched on)
  aiGapCheck: 'ai.gapcheck',
  // F1-FXA (ISO 55001 edition): FXA pushes exceptions; documents linked to FXA records
  exceptionsIngest: 'exceptions.ingest',
  docsLink: 'documents.link',
  // C4 (AGF feedback): uncontrolled copies of controlled documents; approval of held uploads
  docsDownloadUncontrolled: 'documents.downloadUncontrolled',
  docsApproveUploads: 'documents.approveUploads',
  checkoutOverride: 'documents.checkoutOverride',
  reports: 'reports.view',
  audit: 'audit.view',
  integrity: 'integrity.view',
  evidence: 'evidence.export',
  users: 'users.manage',
  groups: 'groups.manage',
  roles: 'roles.manage',
  grants: 'grants.manage',
  /** Organisation settings, including the share-link policy. */
  settings: 'settings.manage',
  supportApprove: 'support.approve',
  apiKeys: 'apikeys.manage',
  importRun: 'import.run',
  platformOrgs: 'platform.orgs',
  platformSupport: 'platform.support',
  platformHealth: 'platform.health',
} as const;

export type PermissionName = (typeof P)[keyof typeof P];
